import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { ReactNode } from "react";

export interface FeatureFlagBoundaryProps {
  /**
   * The name of the feature flag to check
   */
  flag: string;
  /**
   * Content to render when the feature flag is enabled
   */
  children: ReactNode;
  /**
   * Optional fallback content to render when the feature flag is disabled
   * If not provided, nothing will be rendered when the flag is off
   */
  fallback?: ReactNode;
}

/**
 * A boundary component that conditionally renders children based on a feature flag state.
 *
 * When the feature flag is enabled, renders children.
 * When disabled, renders the optional fallback or nothing.
 *
 * @example
 * // Render new feature only if flag is on
 * <FeatureFlagBoundary flag="new-dashboard">
 *   <NewDashboard />
 * </FeatureFlagBoundary>
 *
 * @example
 * // Render new feature if flag is on, otherwise render old version
 * <FeatureFlagBoundary flag="new-dashboard" fallback={<OldDashboard />}>
 *   <NewDashboard />
 * </FeatureFlagBoundary>
 */
export const FeatureFlagBoundary = ({
  flag,
  children,
  fallback = null,
}: FeatureFlagBoundaryProps): ReactNode => {
  const isEnabled = useFeatureIsOn(flag);

  return isEnabled ? children : fallback;
};
